/* eslint-disable react-refresh/only-export-components -- mixed helpers + presentational components by design */
// Shared building blocks for campaign cards / detail page so the My
// Campaigns list and the per-campaign detail render the same visual language.

import React from 'react';
import { createPortal } from 'react-dom';
import type { PublisherConfig, SelfServiceSubmissionRow } from '../../types/selfService';

// --- Submission row shape --------------------------------------------------

// The shared helpers only need a subset of submission fields; allow callers
// to pass anything compatible with `SelfServiceSubmissionRow` (or a Pick of it).
type SharedSubmissionRow = Pick<
  SelfServiceSubmissionRow,
  'id' | 'publisher_slug' | 'contact_email' | 'business_info' | 'campaign'
  | 'status' | 'acknowledged_at' | 'completion_acknowledged_at'
  | 'impressions_delivered' | 'pacing_impressions' | 'line_item_cpm'
  | 'impressions_updated_at' | 'currentFlight'
  | 'canceled_at' | 'payment_plan'
>;

// --- Buckets --------------------------------------------------------------

export type BucketId = 'active' | 'scheduled' | 'completed' | 'pending' | 'archived';

// Visual styling for the bucket filter chips on My Campaigns. Lives here so
// every consumer of `bucketFor` can also reach the colors / labels they
// should pair with.
export interface BucketDef {
  id: BucketId;
  label: string;
  dot: string;          // solid dot color
  tint: string;         // soft bg color when active
  tintRing: string;     // border color when active
  countBg: string;      // count pill bg (active)
  countBgIdle: string;  // count pill bg (inactive)
  countFg: string;      // count text color
  text: string;         // active label color
}

export const ACTIVE_BUCKET_IDS: BucketId[] = ['pending', 'active', 'scheduled'];
export const INACTIVE_BUCKET_IDS: BucketId[] = ['completed', 'archived'];

export const BUCKETS: BucketDef[] = [
  {
    id: 'active',
    label: 'Active',
    dot: '#10b981',
    tint: 'linear-gradient(180deg, #ecfdf5 0%, #d1fae5 100%)',
    tintRing: 'rgba(16, 185, 129, 0.35)',
    countBg: 'rgba(16, 185, 129, 0.15)',
    countBgIdle: 'rgba(16, 185, 129, 0.08)',
    countFg: '#047857',
    text: '#065f46',
  },
  {
    id: 'scheduled',
    label: 'Scheduled',
    dot: '#0ea5e9',
    tint: 'linear-gradient(180deg, #eff6ff 0%, #dbeafe 100%)',
    tintRing: 'rgba(14, 165, 233, 0.35)',
    countBg: 'rgba(14, 165, 233, 0.15)',
    countBgIdle: 'rgba(14, 165, 233, 0.08)',
    countFg: '#0369a1',
    text: '#075985',
  },
  {
    id: 'completed',
    label: 'Completed',
    dot: '#8b5cf6',
    tint: 'linear-gradient(180deg, #faf5ff 0%, #ede9fe 100%)',
    tintRing: 'rgba(139, 92, 246, 0.35)',
    countBg: 'rgba(139, 92, 246, 0.15)',
    countBgIdle: 'rgba(139, 92, 246, 0.08)',
    countFg: '#6d28d9',
    text: '#5b21b6',
  },
  {
    id: 'pending',
    label: 'Pending',
    dot: '#f59e0b',
    tint: 'linear-gradient(180deg, #fffbeb 0%, #fef3c7 100%)',
    tintRing: 'rgba(245, 158, 11, 0.35)',
    countBg: 'rgba(245, 158, 11, 0.18)',
    countBgIdle: 'rgba(245, 158, 11, 0.08)',
    countFg: '#b45309',
    text: '#92400e',
  },
  {
    id: 'archived',
    label: 'Archived',
    dot: '#94a3b8',
    tint: 'linear-gradient(180deg, #f8fafc 0%, #e2e8f0 100%)',
    tintRing: 'rgba(100, 116, 139, 0.35)',
    countBg: 'rgba(100, 116, 139, 0.15)',
    countBgIdle: 'rgba(100, 116, 139, 0.08)',
    countFg: '#475569',
    text: '#334155',
  },
];

const ymdTs = (d?: string | null) => (d ? new Date(d.slice(0, 10) + 'T00:00:00').getTime() : 0);

/** Sort rows within a bucket using the same rules as My Campaigns. */
export function sortRowsForBucket<T extends SharedSubmissionRow & { created_at?: string }>(
  rows: T[],
  bucketId: BucketId,
): T[] {
  const sorted = [...rows];
  switch (bucketId) {
    case 'active':
      sorted.sort((a, b) => ymdTs(a.campaign?.endDate) - ymdTs(b.campaign?.endDate));
      break;
    case 'scheduled':
      sorted.sort((a, b) => ymdTs(a.campaign?.startDate) - ymdTs(b.campaign?.startDate));
      break;
    case 'completed':
      sorted.sort((a, b) => ymdTs(b.campaign?.endDate) - ymdTs(a.campaign?.endDate));
      break;
    case 'pending':
    case 'archived':
      sorted.sort(
        (a, b) =>
          new Date(b.created_at ?? 0).getTime() - new Date(a.created_at ?? 0).getTime(),
      );
      break;
  }
  return sorted;
}

export function bucketFor(s: SharedSubmissionRow): BucketId {
  // CTV-466 follow-up: `creative_rejected` sits in pending alongside
  // payment_authorized — the campaign is awaiting advertiser action (Fix &
  // resubmit) rather than truly archived. Only fully terminal statuses
  // (canceled / refunded / payment_failed / explicit archived) belong in
  // the archived bucket.
  if (s.status === 'payment_authorized' || s.status === 'creative_rejected') return 'pending';
  if (['archived', 'payment_failed', 'payment_refunded', 'canceled'].includes(s.status)) return 'archived';
  if (s.status === 'approved') {
    const now = Date.now();
    const start = s.campaign?.startDate ? new Date(s.campaign.startDate.slice(0, 10) + 'T00:00:00').getTime() : null;
    const end = s.campaign?.endDate ? new Date(s.campaign.endDate.slice(0, 10) + 'T23:59:59').getTime() : null;
    if (start && now < start) return 'scheduled';
    if (end && now > end) return 'completed';
    return 'active';
  }
  return 'pending';
}

export function bucketBadge(s: SharedSubmissionRow): { className: string; label: string } {
  switch (bucketFor(s)) {
    case 'active': return { className: 'bg-emerald-100 text-emerald-800', label: 'Live' };
    case 'scheduled': return { className: 'bg-sky-100 text-sky-800', label: 'Scheduled' };
    case 'completed': return { className: 'bg-violet-100 text-violet-800', label: 'Completed' };
    default: {
      const fallback: Record<string, { className: string; label: string }> = {
        pending_payment: { className: 'bg-slate-100 text-slate-700', label: 'Awaiting Payment' },
        payment_authorized: { className: 'bg-amber-100 text-amber-800', label: 'Under Review' },
        creative_rejected: { className: 'bg-red-100 text-red-800', label: 'Not Approved' },
        payment_failed: { className: 'bg-red-100 text-red-800', label: 'Payment Failed' },
        archived: { className: 'bg-slate-200 text-slate-700', label: 'Canceled' },
        payment_refunded: { className: 'bg-slate-200 text-slate-700', label: 'Refunded' },
        canceled: { className: 'bg-slate-200 text-slate-700', label: 'Canceled' },
      };
      return fallback[s.status] ?? { className: 'bg-slate-100 text-slate-700', label: s.status };
    }
  }
}

// --- Cancellation -----------------------------------------------------------

export type CancelCopyVariant =
  | 'pending_payment'
  | 'payment_authorized'
  | 'approved_pre_flight'
  | 'approved_live_monthly'
  | 'approved_live_no_refund';

// Returns true if the advertiser can cancel this submission right now. Mirrors
// the backend policy in selfservice-api/src/modules/submissions/submissions.service.ts#cancel.
// Both sides MUST agree or the UI will show a button that the API rejects.
export function canCancel(s: SharedSubmissionRow, now: Date = new Date()): boolean {
  return cancelCopyFor(s, now) !== null;
}

// Returns the modal copy variant for the given submission, or null when the
// submission isn't cancelable. Encodes the same decision tree as the backend
// for: terminal status / monthly mid-flight / past end_date.
export function cancelCopyFor(
  s: SharedSubmissionRow,
  now: Date = new Date(),
): CancelCopyVariant | null {
  switch (s.status) {
    case 'pending_payment':
      return 'pending_payment';
    case 'payment_authorized':
      return 'payment_authorized';
    case 'approved': {
      const today = now.toISOString().slice(0, 10);
      const start = s.campaign?.startDate?.slice(0, 10) ?? '';
      const end = s.campaign?.endDate?.slice(0, 10) ?? '';
      if (!start || !end) return null;
      if (today > end) return null; // already over
      if (s.payment_plan === 'monthly') {
        const finalPeriodStart = finalMonthlyPeriodStart(start, end);
        if (!finalPeriodStart || today >= finalPeriodStart) return null;
        return 'approved_live_monthly';
      }
      // TODO(out-of-scope): a live upfront campaign returns null here, so it
      // can't be canceled anywhere in the UI (both CancelButton and the Edit
      // modal's Cancel tab derive their variant from this function). The
      // `approved_live_no_refund` copy variant in CancelCampaignModal is
      // therefore dead/unreachable. Decide whether live upfront campaigns
      // should be stoppable with no refund — if so, return
      // 'approved_live_no_refund' instead of null and wire it in; if not,
      // delete the dead variant + its copy. See CancelButton.test.tsx
      // ("approved + live + upfront") which documents the current behavior.
      if (today >= start) return null;
      return 'approved_pre_flight';
    }
    default:
      // canceled / creative_rejected / payment_failed / payment_refunded / archived
      return null;
  }
}

function finalMonthlyPeriodStart(startDateStr: string, endDateStr: string): string | null {
  const start = parseYmdUtc(startDateStr);
  const end = parseYmdUtc(endDateStr);
  if (!start || !end || end < start) return null;
  let last: Date | null = null;
  for (let n = 0; n < 240; n += 1) {
    const periodStart = anniversaryUtc(start, n);
    if (periodStart > end) break;
    last = periodStart;
  }
  return last ? formatYmdUtc(last) : null;
}

function parseYmdUtc(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.slice(0, 10));
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return null;
  return date;
}

function anniversaryUtc(start: Date, monthsToAdd: number): Date {
  const targetMonthIndex = start.getUTCMonth() + monthsToAdd;
  const year = start.getUTCFullYear() + Math.floor(targetMonthIndex / 12);
  const month = ((targetMonthIndex % 12) + 12) % 12;
  const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  return new Date(Date.UTC(year, month, Math.min(start.getUTCDate(), lastDay)));
}

function formatYmdUtc(date: Date): string {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
}

export const statusToStep = (status: string): number => {
  switch (status) {
    case 'payment_authorized': return 1;
    case 'approved': return 4;
    default: return 0;
  }
};

// Status-driven badge used by the publisher review surfaces (the submissions
// list + detail page). Different copy/colors than `bucketBadge`, which is
// the advertiser-facing variant that switches on date-derived bucket.
const PUBLISHER_STATUS_BADGE: Record<string, { className: string; label: string }> = {
  pending_payment:    { className: 'bg-slate-100 text-slate-700', label: 'Awaiting Payment' },
  payment_authorized: { className: 'bg-amber-100 text-amber-800', label: 'Pending Review' },
  approved:           { className: 'bg-emerald-100 text-emerald-800', label: 'Approved' },
  payment_failed:     { className: 'bg-red-100 text-red-800', label: 'Payment Failed' },
  creative_rejected:  { className: 'bg-red-100 text-red-800', label: 'Rejected' },
  archived:           { className: 'bg-slate-200 text-slate-700', label: 'Canceled' },
  payment_refunded:   { className: 'bg-slate-200 text-slate-700', label: 'Refunded' },
  canceled:           { className: 'bg-slate-200 text-slate-700', label: 'Canceled' },
};

export function publisherStatusBadge(status: string): { className: string; label: string } {
  return PUBLISHER_STATUS_BADGE[status] ?? { className: 'bg-slate-100 text-slate-700', label: status };
}

// --- Campaign dates / delivery (DB-backed) --------------------------------

// Returns the campaign's start/end as stored on the submission. Empty string
// when missing — UI should fall back to '—'. This is the *purchased* window
// and is what the stat row (budget / dates / CPM / targeting) displays.
export function campaignDates(s: SharedSubmissionRow): { startDate: string; endDate: string } {
  return { startDate: s.campaign?.startDate ?? '', endDate: s.campaign?.endDate ?? '' };
}

// Ad-play progress for the bar. Reads order-level SUMs across every flight on
// the submission (ADR-0016, CTV-550) so monthly buyers see lifetime progress
// against their full purchased goal rather than the current month in isolation.
export function campaignDelivery(s: SharedSubmissionRow) {
  const current = s.impressions_delivered ?? 0;
  const total = s.pacing_impressions ?? null;
  const percent =
    total != null && total > 0 ? Math.min(100, (current / total) * 100) : null;
  return { current, total, percent, hasData: total != null };
}

// --- Follow-up campaign prefill (Rerun) -----------------------------------

// Parses a YYYY-MM-DD string as local midnight. Returns null for anything
// that isn't a recognizable date — callers use this signal to skip prefill
// rather than render NaN-dates.
function parseYmdLocal(ymd: string | null | undefined): Date | null {
  if (!ymd || typeof ymd !== 'string') return null;
  const short = ymd.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(short)) return null;
  const d = new Date(`${short}T00:00:00`);
  return isNaN(d.getTime()) ? null : d;
}

function formatYmdLocal(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function addDaysLocal(d: Date, days: number): Date {
  const next = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  next.setDate(next.getDate() + days);
  return next;
}

// Strips a trailing " — Mon YYYY", " (copy)", or " (rerun N)" suffix from the
// Source campaign name, then appends " — {Mon YYYY of startDateYmd}". Loops
// the strip until no more suffixes match so legacy names like
// "Spring (copy) (copy)" collapse cleanly. Returns '' for an empty source
// name; returns the stripped name unchanged if startDateYmd is unparseable
// (better to show the bare name than a half-baked stamp).
//
// See docs/adr/0001-rerun-campaign-naming-rule.md for the rationale.
export function buildFollowUpCampaignName(sourceName: string, startDateYmd: string): string {
  const trimmed = (sourceName ?? '').trim();
  if (!trimmed) return '';

  const suffixPatterns: RegExp[] = [
    /\s+—\s+[A-Z][a-z]{2}\s+\d{4}$/,
    /\s+\(copy\)$/i,
    /\s+\(rerun\s+\d+\)$/i,
  ];
  let stripped = trimmed;
  for (;;) {
    const before = stripped;
    for (const re of suffixPatterns) stripped = stripped.replace(re, '');
    if (stripped === before) break;
  }

  const start = parseYmdLocal(startDateYmd);
  if (!start) return stripped;
  const monthYear = start.toLocaleString('en-US', { month: 'short', year: 'numeric' });
  return `${stripped} — ${monthYear}`;
}

// Returns the Follow-up campaign's prefilled date window. The rule:
//   start = max(sourceEnd + 1 day, today + 3 days)
//   end   = start + (sourceEnd - sourceStart) days
// Both ends are local-midnight YYYY-MM-DD strings. The +3-day floor matches
// the wizard's `minStartDate` rule (publisher needs review headroom); using
// sourceEnd+1 when the source is still running keeps the schedule contiguous.
//
// Returns empty strings if either input is missing or malformed — the wizard
// then falls back to its normal empty-state defaults.
export function buildFollowUpDates(
  sourceStart: string | null | undefined,
  sourceEnd: string | null | undefined,
  today: Date = new Date(),
): { startDate: string; endDate: string } {
  const start = parseYmdLocal(sourceStart);
  const end = parseYmdLocal(sourceEnd);
  if (!start || !end) return { startDate: '', endDate: '' };

  const todayLocal = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const afterSourceEnd = addDaysLocal(end, 1);
  const afterToday = addDaysLocal(todayLocal, 3);
  const newStart = afterSourceEnd.getTime() >= afterToday.getTime() ? afterSourceEnd : afterToday;

  const lengthDays = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
  const newEnd = addDaysLocal(newStart, lengthDays);

  return { startDate: formatYmdLocal(newStart), endDate: formatYmdLocal(newEnd) };
}

// --- Optimistic Blanket extension --------------------------------------

// CTV-488 / CTV-504: mirrors `applyBlanketCreativeExtension` on the server
// (submissions.service.ts) for client-side optimistic state. After a
// successful extend the backend bumps `creative_end_date` (and the last
// `segment_shares` entry's `end_date`) on every Last-running creative —
// active creatives whose window covers the previous end date. Without
// mirroring it here, the local row keeps stale creative dates, and the
// next time the Edit modal opens its receipt step filter (`creative_end_date
// >= currentEndDay`) hides every creative and flashes the "No active
// commercials" warning until a focus-event silent refetch lands.
export function applyOptimisticBlanketExtension<
  T extends Pick<SelfServiceSubmissionRow, 'creatives'>,
>(row: T, previousEndDate: string, newEndDate: string): T {
  const creatives = row.creatives;
  if (!creatives || creatives.length === 0) return row;
  return {
    ...row,
    creatives: creatives.map((c) => {
      if (
        c.status !== 'active'
        || c.creative_start_date > previousEndDate
        || c.creative_end_date < previousEndDate
      ) {
        return c;
      }
      const segs = Array.isArray(c.segment_shares) ? c.segment_shares : [];
      const nextSegs = segs.length === 0
        ? [{ start_date: previousEndDate, end_date: newEndDate, share: 100 }]
        : segs.map((s, i, arr) =>
            i === arr.length - 1 ? { ...s, end_date: newEndDate } : s,
          );
      return { ...c, creative_end_date: newEndDate, segment_shares: nextSegs };
    }),
  };
}

// --- Visual components ----------------------------------------------------

export function Stat({ label, value, accentColor, showRail }: { label: string; value: string; accentColor?: string; showRail?: boolean }) {
  const accent = accentColor ?? '#94a3b8';
  return (
    <div className="relative px-4 min-w-0">
      {showRail && (
        <span
          aria-hidden
          className="absolute left-0 top-1 bottom-1 w-px"
          style={{ background: `linear-gradient(180deg, transparent, ${accent}66, transparent)` }}
        />
      )}
      <div className="text-[10px] font-medium uppercase tracking-[0.1em] text-slate-400 mb-1">{label}</div>
      <div className="text-[13px] font-semibold tracking-tight text-slate-900 truncate">{value || '—'}</div>
    </div>
  );
}

interface AdPlayProgressProps {
  current: number;
  total: number;
  percent: number | null;
  startDate?: string;
  endDate?: string;
  prominent?: boolean;
  hideDaysRemaining?: boolean;
}

export function daysBetween(later: Date, earlier: Date) {
  return Math.ceil((later.getTime() - earlier.getTime()) / (1000 * 60 * 60 * 24));
}

// Re-exported for back-compat with consumers that imported from this module;
// the single source of truth lives in shared/dateFormat.ts.
import { formatShortDate } from './shared/dateFormat';
export { formatShortDate };

export const AdPlayProgress: React.FC<AdPlayProgressProps> = ({ current, total, percent, startDate, endDate, prominent = false, hideDaysRemaining = false }) => {
  const targetPct = Math.max(0, Math.min(100, percent ?? 0));
  const [displayPct, setDisplayPct] = React.useState(0);
  const [showFireworks, setShowFireworks] = React.useState(false);
  const [showStamp, setShowStamp] = React.useState(false);
  const [inView, setInView] = React.useState(false);
  const rootRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (inView) return;
    const el = rootRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') { setInView(true); return; }
    const observer = new IntersectionObserver(
      (entries) => { if (entries.some((e) => e.isIntersecting)) { setInView(true); observer.disconnect(); } },
      { threshold: 0.25 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [inView]);

  const FILL_DELAY = 400;
  React.useEffect(() => {
    if (!inView) return;
    const id = window.setTimeout(() => setDisplayPct(targetPct), FILL_DELAY);
    return () => window.clearTimeout(id);
  }, [inView, targetPct]);
  React.useEffect(() => {
    if (!inView || targetPct < 100) { setShowFireworks(false); setShowStamp(false); return; }
    const fId = window.setTimeout(() => setShowFireworks(true), FILL_DELAY + 2200);
    const sId = window.setTimeout(() => setShowStamp(true), FILL_DELAY + 2200 + 750);
    return () => { window.clearTimeout(fId); window.clearTimeout(sId); };
  }, [inView, targetPct]);

  const isEmpty = targetPct <= 0;
  let daysRemaining: number | null = null;
  if (endDate && !hideDaysRemaining) {
    const end = new Date(endDate.slice(0, 10) + 'T23:59:59');
    daysRemaining = Math.max(0, daysBetween(end, new Date()));
  }

  return (
    <div ref={rootRef}>
      <div className={`flex items-baseline justify-between ${prominent ? 'mb-3' : 'mb-2'}`}>
        {daysRemaining !== null ? (
          <div className={`font-medium uppercase tracking-[0.12em] text-slate-400 ${prominent ? 'text-[11px]' : 'text-[10px]'}`}>
            <span className="text-slate-700 font-semibold tabular-nums">{daysRemaining}</span>{' '}
            {daysRemaining === 1 ? 'day' : 'days'} remaining
          </div>
        ) : <span />}
        <div className={`flex items-baseline gap-2 tabular-nums text-slate-500 ${prominent ? 'text-[14px]' : 'text-[12px]'}`}>
          <span className={`font-medium uppercase tracking-[0.12em] text-slate-400 ${prominent ? 'text-[11px]' : 'text-[10px]'}`}>
            Ad plays
          </span>
          <span>
            <span className="text-slate-900 font-semibold">{current.toLocaleString()}</span>
            <span className="mx-1 text-slate-400">/</span>
            <span>{total.toLocaleString()}</span>
            <span className={`ml-2 font-semibold transition-colors ${isEmpty ? 'text-slate-400' : 'text-emerald-600'}`}>
              {targetPct.toFixed(0)}%
            </span>
          </span>
        </div>
      </div>
      <div className={`relative w-full pt-3 pb-1`}>
        <div
          className={`relative w-full rounded-full overflow-hidden ${prominent ? 'h-5' : 'h-3.5'}`}
          style={{
            background: 'linear-gradient(180deg, #e2e8f0 0%, #f1f5f9 100%)',
            boxShadow: 'inset 0 2px 4px rgba(15, 23, 42, 0.22), inset 0 1px 1px rgba(15, 23, 42, 0.1), inset 0 -1px 0 rgba(255, 255, 255, 0.85)',
          }}
        >
          <div
            className="absolute inset-y-0 left-0 rounded-full transition-all duration-[2200ms] ease-out overflow-hidden"
            style={{
              width: `${displayPct}%`,
              background: 'linear-gradient(180deg, #a7f3d0 0%, #34d399 28%, #10b981 60%, #047857 100%)',
              boxShadow: displayPct > 0
                ? '0 0 18px -2px rgba(16, 185, 129, 0.75), 0 2px 6px -2px rgba(4, 120, 87, 0.5), 0 1px 0 rgba(255, 255, 255, 0.7) inset, 0 -2px 4px rgba(0, 0, 0, 0.25) inset, 0 0 0 0.5px rgba(4, 120, 87, 0.4)'
                : 'none',
            }}
          >
            {displayPct > 0 && (
              <>
                {/* Static gloss along the top half of the fill. */}
                <span aria-hidden className="absolute inset-x-0 top-0 h-1/2 rounded-t-full pointer-events-none"
                  style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0) 100%)' }} />
                {/* Shimmer pass — translates a soft white highlight across the
                    fill on a slow loop. Stops once the bar reaches 100% so
                    the celebration moment isn't drowned by motion. */}
                {displayPct < 100 && (
                  <span
                    aria-hidden
                    className="absolute inset-y-0 left-0 w-full pointer-events-none"
                    style={{
                      background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.55) 50%, transparent 100%)',
                      animation: 'progress-shimmer 2.4s linear infinite',
                      mixBlendMode: 'overlay',
                    }}
                  />
                )}
              </>
            )}
          </div>
        </div>
        {targetPct > 0 && targetPct < 100 && (
          <div
            className="pointer-events-none absolute top-1/2 transition-all duration-[2200ms] ease-out z-10"
            style={{
              left: `${Math.min(displayPct, 92)}%`,
              transform: 'translate(0, -50%)',
              opacity: displayPct > 0 ? 1 : 0,
            }}
          >
            <div className="inline-flex items-center gap-1.5 bg-white border border-emerald-200 rounded-full pl-1.5 pr-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-emerald-700 whitespace-nowrap"
              style={{
                background: 'linear-gradient(180deg, #ffffff 0%, #f0fdf4 100%)',
                boxShadow: '0 6px 16px -4px rgba(16, 185, 129, 0.55), 0 2px 4px -1px rgba(15, 23, 42, 0.15), 0 1px 0 rgba(255,255,255,0.9) inset, 0 -1px 0 rgba(16, 185, 129, 0.15) inset',
              }}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" style={{ boxShadow: '0 0 6px rgba(16, 185, 129, 0.8)' }} />
              On pace
            </div>
          </div>
        )}
        {showStamp && (
          <div className="pointer-events-none absolute top-1/2 left-1/2 z-10" style={{ transform: 'translate(-50%, -50%)' }}>
            <div className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-[12px] font-semibold uppercase tracking-[0.14em] whitespace-nowrap"
              style={{
                background: 'linear-gradient(180deg, #ffffff 0%, #f0fdf4 100%)',
                color: '#047857',
                boxShadow: '0 8px 22px -4px rgba(16, 185, 129, 0.6), 0 3px 6px -1px rgba(15, 23, 42, 0.2), 0 1px 0 rgba(255,255,255,0.95) inset, 0 -1px 0 rgba(16, 185, 129, 0.2) inset, 0 0 0 1.5px rgba(16, 185, 129, 0.4)',
                animation: 'card-stamp-in 500ms cubic-bezier(0.2, 0.9, 0.3, 1.2) both',
                transformOrigin: 'center',
              }}
            >
              <span className="w-2 h-2 rounded-full bg-emerald-500" style={{ boxShadow: '0 0 8px rgba(16, 185, 129, 0.9)' }} />
              Delivered in full
            </div>
            {/* Rubber-stamp slam: oversized + rotated entrance that snaps down
                and settles, so the badge reads as stamped on rather than faded in. */}
            <style>{`
              @keyframes card-stamp-in {
                0%   { opacity: 0; transform: scale(2.4) rotate(-11deg); }
                55%  { opacity: 1; transform: scale(0.9) rotate(3deg); }
                78%  { transform: scale(1.05) rotate(-1.5deg); }
                100% { opacity: 1; transform: scale(1) rotate(0deg); }
              }
            `}</style>
          </div>
        )}
        {showFireworks && (
          <FireworksBurst />
        )}
      </div>
      {(startDate || endDate) && (
        <div className="mt-1.5 flex items-center justify-between text-[10px] font-medium uppercase tracking-[0.08em] text-slate-400 px-0.5">
          <span>{formatShortDate(startDate)}</span>
          <span>{formatShortDate(endDate)}</span>
        </div>
      )}
    </div>
  );
};

const FireworksBurst: React.FC = () => (
  <div
    className="pointer-events-none absolute top-1/2 z-10"
    style={{ right: '0', transform: 'translate(50%, -50%)' }}
    aria-hidden
  >
    <span
      className="absolute top-1/2 left-1/2 rounded-full"
      style={{
        transform: 'translate(-50%, -50%)',
        background: 'radial-gradient(circle, rgba(255,255,255,1), rgba(110,231,183,0.75) 30%, rgba(16,185,129,0.45) 55%, transparent 75%)',
        animation: 'card-fireworks-flash 700ms ease-out both',
      }}
    />
    <style>{`
      @keyframes card-fireworks-flash {
        0%   { width: 8px; height: 8px; opacity: 0; }
        20%  { width: 70px; height: 70px; opacity: 1; }
        100% { width: 180px; height: 180px; opacity: 0; }
      }
    `}</style>
  </div>
);

// --- Live pulse keyframes (one global definition) -------------------------

export const SharedKeyframes: React.FC = () => (
  <style>{`
    @keyframes live-pulse {
      0%   { transform: scale(1); box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.55); }
      40%  { transform: scale(1.18); box-shadow: 0 0 0 8px rgba(16, 185, 129, 0); }
      100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); }
    }
    .live-pulse { animation: live-pulse 3.5s ease-out infinite; will-change: transform; }

    @keyframes progress-shimmer {
      0%   { transform: translateX(-100%); }
      100% { transform: translateX(100%); }
    }
  `}</style>
);

// re-export so consumers don't have to import from react-dom directly
export { createPortal };
// keep types in sync with usage sites
export type { PublisherConfig };
